<template>
  <div class="settings--content">
    <loading-state v-if="uiFlags.isFetching || uiFlags.isFetchingAgentBot" />
    <form v-else class="row" @submit.prevent="updateActiveAgentBot">
      <settings-section
        :title="$t('INBOX_MGMT.BOT_CONFIGURATION.TITLE')"
        :sub-title="$t('INBOX_MGMT.BOT_CONFIGURATION.DESC')"
      >
        <div class="medium-7 columns">
          <label>
            <select v-model="selectedAgentBotId">
              <option value="" disabled selected>{{
                $t('INBOX_MGMT.BOT_CONFIGURATION.SELECT_PLACEHOLDER')
              }}</option>
              <option
                v-for="agentBot in agentBots"
                :key="agentBot.id"
                :value="agentBot.id"
              >
                {{ agentBot.name }}
              </option>
            </select>
          </label>
          <div class="button-container">
            <woot-submit-button
              :button-text="$t('INBOX_MGMT.BOT_CONFIGURATION.SUBMIT')"
              :loading="uiFlags.isSettingAgentBot"
            />
            <woot-button
              type="button"
              :disabled="!selectedAgentBotId"
              :loading="uiFlags.isDisconnecting"
              variant="smooth"
              color-scheme="alert"
              class="button--disconnect"
              @click="disconnectBot"
            >
              {{ $t('INBOX_MGMT.BOT_CONFIGURATION.DISCONNECT') }}
            </woot-button>
          </div>
        </div>
      </settings-section>

      <settings-section
        :title="$t('INBOX_MGMT.BOT_CONFIGURATION.MESSAGE_LOCK.TITLE')"
        :sub-title="$t('INBOX_MGMT.BOT_CONFIGURATION.MESSAGE_LOCK.DESC')"
      >
        <div class="medium-7 columns">
          <div class="enter-to-send--checkbox">
            <input
              id="botMessageLock"
              v-model="botMessageLock"
              type="checkbox"
              @change="updateBotLockSettings"
            />
            <label for="botMessageLock">
              {{ $t('INBOX_MGMT.BOT_CONFIGURATION.MESSAGE_LOCK.LABEL') }}
            </label>
          </div>
          <div v-if="botMessageLock" class="lock-time--container">
            <label>
              {{ $t('INBOX_MGMT.BOT_CONFIGURATION.MESSAGE_LOCK.TIME_LABEL') }}
              <input
                v-model.number="botLockTime"
                type="number"
                min="1"
                class="lock-time--input"
                @change="updateBotLockSettings"
              />
              <span class="lock-time--unit">
                {{ $t('INBOX_MGMT.BOT_CONFIGURATION.MESSAGE_LOCK.TIME_UNIT') }}
              </span>
            </label>
          </div>
        </div>
      </settings-section>
    </form>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import SettingsSection from 'dashboard/components/SettingsSection';
import LoadingState from 'dashboard/components/widgets/LoadingState';
import alertMixin from 'shared/mixins/alertMixin';

export default {
  components: {
    LoadingState,
    SettingsSection,
  },
  mixins: [alertMixin],
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      selectedAgentBotId: null,
      botMessageLock: false,
      botLockTime: 30,
      isUpdatingLock: false,
    };
  },
  computed: {
    ...mapGetters({
      agentBots: 'agentBots/getBots',
      uiFlags: 'agentBots/getUIFlags',
    }),
    activeAgentBot() {
      return this.$store.getters['agentBots/getActiveAgentBot'](this.inbox.id);
    },
  },
  watch: {
    activeAgentBot() {
      this.selectedAgentBotId = this.activeAgentBot.id;
    },
    inbox(newVal, oldVal) {
      if (!this.isUpdatingLock && newVal.id !== oldVal.id) {
        this.setDefaults();
      }
    },
  },
  mounted() {
    this.$store.dispatch('agentBots/get');
    this.$store.dispatch('agentBots/fetchAgentBotInbox', this.inbox.id);
    this.setDefaults();
  },

  methods: {
    setDefaults() {
      this.botMessageLock = this.inbox.bot_message_lock || false;
      this.botLockTime = this.inbox.bot_lock_time != null ? this.inbox.bot_lock_time : 30;
    },
    async updateActiveAgentBot() {
      try {
        await this.$store.dispatch('agentBots/setAgentBotInbox', {
          inboxId: this.inbox.id,
          // Added this to make sure that empty values are not sent to the API
          botId: this.selectedAgentBotId ? this.selectedAgentBotId : undefined,
        });
        this.showAlert(this.$t('INBOX_MGMT.BOT_CONFIGURATION.SUCCESS_MESSAGE'));
      } catch (error) {
        this.showAlert(this.$t('INBOX_MGMT.BOT_CONFIGURATION.ERROR_MESSAGE'));
      }
    },
    async disconnectBot() {
      try {
        await this.$store.dispatch('agentBots/disconnectBot', {
          inboxId: this.inbox.id,
        });
        this.showAlert(
          this.$t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECTED_SUCCESS_MESSAGE')
        );
      } catch (error) {
        this.showAlert(
          error?.message ||
            this.$t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECTED_ERROR_MESSAGE')
        );
      }
    },
    async updateBotLockSettings() {
      this.isUpdatingLock = true;
      try {
        await this.$store.dispatch('inboxes/updateInbox', {
          id: this.inbox.id,
          formData: false,
          bot_message_lock: this.botMessageLock,
          bot_lock_time: this.botLockTime,
        });
        this.showAlert(
          this.$t('INBOX_MGMT.BOT_CONFIGURATION.MESSAGE_LOCK.SUCCESS_MESSAGE')
        );
      } catch (error) {
        this.showAlert(
          this.$t('INBOX_MGMT.BOT_CONFIGURATION.MESSAGE_LOCK.ERROR_MESSAGE')
        );
      } finally {
        this.isUpdatingLock = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.button--disconnect {
  margin-left: var(--space-small);
}

.lock-time--container {
  margin-top: var(--space-small);

  label {
    display: flex;
    align-items: center;
    gap: var(--space-small);
  }
}

.lock-time--input {
  width: 80px;
  margin-bottom: 0;
}

.lock-time--unit {
  color: var(--b-500);
  font-size: var(--font-size-small);
}
</style>
